import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// import { ClientCreate, ClientUpdate } from '../../lib/types/api';
import { useCreateClient, useUpdateClient } from '../../lib/hooks/useApi';
import { Plus, Trash2, Users, Mail, Phone, Building2, Sparkles } from 'lucide-react';

// SPOC validation schema
const spocSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, 'SPOC name is required').max(255, 'SPOC name is too long'),
  email: z.string().email('Invalid email format').optional().or(z.literal('')),
  phone: z.string().optional(),
});

// Main validation schema
const clientSchema = z.object({
  client_name: z.string().min(1, 'Client name is required').max(255, 'Client name is too long'),
  spoc_name: z.string().min(1, 'Primary SPOC name is required').max(255, 'Primary SPOC name is too long'),
  contact_email: z.string().email('Invalid email format').optional().or(z.literal('')),
  contact_phone: z.string().optional(),
  spocs: z.array(spocSchema).min(1, 'At least one SPOC is required'),
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    country: z.string().optional(),
    zip_code: z.string().optional(),
  }).optional(),
  notes: z.string().optional(),
  status: z.enum(['active', 'inactive']).default('active'),
});

type ClientFormData = z.infer<typeof clientSchema>;

interface ClientFormProps {
  editData?: any;
  mode?: 'create' | 'edit';
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const ClientForm: React.FC<ClientFormProps> = ({
  editData,
  mode = 'create',
  onSuccess,
  onCancel,
}) => {
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const [isLoaded, setIsLoaded] = useState(false);

  // Initialize SPOCs from editData or create default
  const initializeSpocs = () => {
    if (editData?.spocs && editData.spocs.length > 0) {
      // Return existing SPOCs from the database
      return editData.spocs.map((spoc: any) => ({
        id: spoc.id || undefined,
        name: spoc.name || '',
        email: spoc.email || '',
        phone: spoc.phone || '',
      }));
    }
    // If no SPOCs in editData but we have spoc_name, create one from legacy fields
    if (editData?.spoc_name) {
      return [{
        name: editData.spoc_name,
        email: editData.contact_email || '',
        phone: editData.contact_phone || '',
      }];
    }
    // Default empty SPOC for new clients
    return [{ name: '', email: '', phone: '' }];
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      client_name: editData?.client_name || '',
      spoc_name: editData?.spoc_name || '',
      contact_email: editData?.contact_email || '',
      contact_phone: editData?.contact_phone || '',
      spocs: initializeSpocs(),
      address: {
        street: editData?.address?.street || '',
        city: editData?.address?.city || '',
        state: editData?.address?.state || '',
        country: editData?.address?.country || '',
        zip_code: editData?.address?.zip_code || '',
      },
      notes: editData?.notes || '',
      status: editData?.status || 'active',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'spocs',
  });

  // Animation trigger
  React.useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Debug logging for editData
  React.useEffect(() => {
    if (editData) {
      console.log('🔍 ClientForm: Edit data received:', editData);
      console.log('🔍 ClientForm: SPOCs in editData:', editData.spocs);
      console.log('🔍 ClientForm: Initialized SPOCs:', initializeSpocs());
    }
  }, [editData]);

  const onSubmit = async (data: ClientFormData) => {
    try {
      console.log('🔍 ClientForm: Form data before processing:', data);
      
      // Clean up empty strings and filter out empty SPOCs
      const generateSpocId = () => (typeof crypto !== 'undefined' && (crypto as any).randomUUID ? (crypto as any).randomUUID() : `spoc-${Math.random().toString(36).slice(2, 10)}`);
      const cleanedSpocs = data.spocs
        .filter(spoc => spoc.name.trim() !== '')
        .map(spoc => ({
          id: spoc.id?.toString().trim() || generateSpocId(),
          name: spoc.name,
          email: spoc.email?.trim() || undefined,
          phone: spoc.phone?.trim() || undefined,
        }));

      console.log('🔍 ClientForm: Cleaned SPOCs:', cleanedSpocs);

      const cleanedData = {
        ...data,
        spocs: cleanedSpocs,
        contact_email: data.contact_email || undefined,
        contact_phone: data.contact_phone || undefined,
        notes: data.notes || undefined,
        address: data.address && Object.values(data.address).some(v => v) ? data.address : undefined,
      };

      console.log('🔍 ClientForm: Final data to send:', cleanedData);

      if (mode === 'create') {
        console.log('🔍 ClientForm: Creating new client...');
        await createClient.mutateAsync(cleanedData);
      } else if (editData?.id) {
        console.log('🔍 ClientForm: Updating client with ID:', editData.id);
        await updateClient.mutateAsync({ id: editData.id, data: cleanedData });
      }

      reset();
      onSuccess?.();
    } catch (error) {
      console.error('❌ ClientForm: Error saving client:', error);
    }
  };

  const addSpoc = () => {
    append({ name: '', email: '', phone: '' });
  };

  const removeSpoc = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <div className={`glass-card p-8 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Main Information Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-xl">
              <Building2 className="w-6 h-6 text-primary-400" />
            </div>
            <h3 className="text-heading-md font-semibold text-text-enterprise-primary">Client Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client Name */}
            <div className="space-y-2">
              <label htmlFor="client_name" className="block text-body-md font-medium text-text-enterprise-primary">
                Client Name *
              </label>
              <input
                {...register('client_name')}
                type="text"
                id="client_name"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter client company name"
              />
              {errors.client_name && (
                <p className="text-body-sm text-error-500 flex items-center space-x-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  <span>{errors.client_name.message}</span>
                </p>
              )}
            </div>

            {/* Primary Contact Email */}
            <div className="space-y-2">
              <label htmlFor="contact_email" className="block text-body-md font-medium text-text-enterprise-primary">
                Primary Contact Email
              </label>
              <input
                {...register('contact_email')}
                type="email"
                id="contact_email"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter primary contact email"
              />
              {errors.contact_email && (
                <p className="text-body-sm text-error-500 flex items-center space-x-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  <span>{errors.contact_email.message}</span>
                </p>
              )}
            </div>

            {/* Primary Contact Phone */}
            <div className="space-y-2">
              <label htmlFor="contact_phone" className="block text-body-md font-medium text-text-enterprise-primary">
                Primary Contact Phone
              </label>
              <input
                {...register('contact_phone')}
                type="tel"
                id="contact_phone"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="+91XXXXXXXXXX"
                defaultValue={'+91'}
              />
              {errors.contact_phone && (
                <p className="text-body-sm text-error-500 flex items-center space-x-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  <span>{errors.contact_phone.message}</span>
                </p>
              )}
            </div>

            {/* Legacy SPOC Name (for backward compatibility) */}
            <div className="space-y-2">
              <label htmlFor="spoc_name" className="block text-body-md font-medium text-text-enterprise-primary">
                Primary SPOC Name *
              </label>
              <input
                {...register('spoc_name')}
                type="text"
                id="spoc_name"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter primary SPOC name"
              />
              {errors.spoc_name && (
                <p className="text-body-sm text-error-500 flex items-center space-x-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  <span>{errors.spoc_name.message}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* SPOCs Section */}
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-secondary-500/20 to-primary-500/20 rounded-xl">
                <Users className="w-6 h-6 text-secondary-400" />
              </div>
              <div>
                <h3 className="text-heading-md font-semibold text-text-enterprise-primary">SPOCs (Single Point of Contact)</h3>
                <p className="text-sm text-text-enterprise-secondary">
                  {fields.length} SPOC{fields.length !== 1 ? 's' : ''} configured
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={addSpoc}
              className="group flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border border-primary-500/30 text-text-enterprise-primary rounded-xl hover:shadow-glow-primary hover:scale-105 transition-all duration-300"
            >
              <Plus className="w-4 h-4" />
              <span>Add SPOC</span>
              <Sparkles className="w-4 h-4 animate-pulse" />
            </button>
          </div>

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="glass-card p-6 relative overflow-hidden group animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Animated Background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute -top-5 -right-5 w-16 h-16 bg-gradient-to-br from-primary-500/5 to-secondary-500/3 rounded-full blur-xl group-hover:scale-150 transition-transform duration-700"></div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-glass-surface/50 rounded-lg">
                        <Users className="w-4 h-4 text-primary-400" />
                      </div>
                      <span className="text-sm font-medium text-text-enterprise-secondary">
                        SPOC #{index + 1}
                      </span>
                    </div>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSpoc(index)}
                        className="p-2 text-text-enterprise-tertiary hover:text-error-400 hover:bg-glass-surface/50 rounded-lg transition-all duration-300"
                        title="Remove SPOC"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* SPOC Name */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-text-enterprise-primary">
                        Name *
                      </label>
                      <input
                        {...register(`spocs.${index}.name`)}
                        type="text"
                        className="glass-input h-10 w-full rounded-lg px-3 text-sm text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                        placeholder="Enter SPOC name"
                      />
                      {errors.spocs?.[index]?.name && (
                        <p className="text-xs text-error-500 flex items-center space-x-1">
                          <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                          <span>{errors.spocs[index]?.name?.message}</span>
                        </p>
                      )}
                    </div>

                    {/* SPOC Email */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-text-enterprise-primary">
                        Email
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary" />
                        <input
                          {...register(`spocs.${index}.email`)}
                          type="email"
                          className="glass-input h-10 w-full rounded-lg pl-10 pr-3 text-sm text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                          placeholder="Enter email"
                        />
                      </div>
                      {errors.spocs?.[index]?.email && (
                        <p className="text-xs text-error-500 flex items-center space-x-1">
                          <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                          <span>{errors.spocs[index]?.email?.message}</span>
                        </p>
                      )}
                    </div>

                    {/* SPOC Phone */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-text-enterprise-primary">
                        Phone
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-enterprise-tertiary" />
                        <input
                          {...register(`spocs.${index}.phone`)}
                          type="tel"
                          className="glass-input h-10 w-full rounded-lg pl-10 pr-3 text-sm text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                          placeholder="Enter phone"
                        />
                      </div>
                      {errors.spocs?.[index]?.phone && (
                        <p className="text-xs text-error-500 flex items-center space-x-1">
                          <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                          <span>{errors.spocs[index]?.phone?.message}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {errors.spocs && (
            <p className="text-body-sm text-error-500 flex items-center space-x-1">
              <span className="w-1 h-1 bg-error-500 rounded-full"></span>
              <span>{errors.spocs.message}</span>
            </p>
          )}
        </div>

        {/* Address Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-success-500/20 to-primary-500/20 rounded-xl">
              <Building2 className="w-6 h-6 text-success-400" />
            </div>
            <h3 className="text-heading-md font-semibold text-text-enterprise-primary">Address Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="address.street" className="block text-body-md font-medium text-text-enterprise-primary">
                Street Address
              </label>
              <input
                {...register('address.street')}
                type="text"
                id="address.street"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter street address"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="address.city" className="block text-body-md font-medium text-text-enterprise-primary">
                City
              </label>
              <input
                {...register('address.city')}
                type="text"
                id="address.city"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter city"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="address.state" className="block text-body-md font-medium text-text-enterprise-primary">
                State/Province
              </label>
              <input
                {...register('address.state')}
                type="text"
                id="address.state"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter state or province"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="address.country" className="block text-body-md font-medium text-text-enterprise-primary">
                Country
              </label>
              <input
                {...register('address.country')}
                type="text"
                id="address.country"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter country"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="address.zip_code" className="block text-body-md font-medium text-text-enterprise-primary">
                ZIP/Postal Code
              </label>
              <input
                {...register('address.zip_code')}
                type="text"
                id="address.zip_code"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
                placeholder="Enter ZIP or postal code"
              />
            </div>
          </div>
        </div>

        {/* Status and Notes Section */}
        <div className="space-y-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-warning-500/20 to-success-500/20 rounded-xl">
              <Building2 className="w-6 h-6 text-warning-400" />
            </div>
            <h3 className="text-heading-md font-semibold text-text-enterprise-primary">Additional Information</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Status */}
            <div className="space-y-2">
              <label htmlFor="status" className="block text-body-md font-medium text-text-enterprise-primary">
                Status
              </label>
              <select
                {...register('status')}
                id="status"
                className="glass-input h-12 w-full rounded-xl px-4 text-body-md text-text-enterprise-primary transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none"
              >
                <option value="active" className="bg-surface-200 text-text-enterprise-primary">Active</option>
                <option value="inactive" className="bg-surface-200 text-text-enterprise-primary">Inactive</option>
              </select>
              {errors.status && (
                <p className="text-body-sm text-error-500 flex items-center space-x-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  <span>{errors.status.message}</span>
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label htmlFor="notes" className="block text-body-md font-medium text-text-enterprise-primary">
                Notes
              </label>
              <textarea
                {...register('notes')}
                id="notes"
                rows={4}
                className="glass-input w-full rounded-xl px-4 py-3 text-body-md text-text-enterprise-primary placeholder:text-text-enterprise-muted transition-all duration-200 ease-out focus:border-primary-500 focus:shadow-glow-primary focus:outline-none resize-none"
                placeholder="Enter any additional notes about the client"
              />
              {errors.notes && (
                <p className="text-body-sm text-error-500 flex items-center space-x-1">
                  <span className="w-1 h-1 bg-error-500 rounded-full"></span>
                  <span>{errors.notes.message}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-8 border-t border-glass-border/30">
          <button
            type="button"
            onClick={onCancel}
            className="group relative overflow-hidden px-6 py-3 text-body-md font-medium text-text-enterprise-primary border border-glass-border/40 rounded-xl hover:shadow-glow-primary hover:border-primary-500/30 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-300 backdrop-blur-xl"
          >
            <span className="relative z-10">Cancel</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
          </button>
          <button
            type="submit"
            disabled={isSubmitting || createClient.isPending || updateClient.isPending}
            className="group relative overflow-hidden bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 border border-primary-500/30 text-text-enterprise-primary px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-glow-primary hover:border-primary-500/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <div className="flex items-center space-x-2 relative z-10">
              <span>
                {isSubmitting || createClient.isPending || updateClient.isPending
                  ? 'Saving...'
                  : mode === 'create'
                  ? 'Create Client'
                  : 'Update Client'}
              </span>
              <Sparkles className="w-4 h-4 animate-pulse" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
          </button>
        </div>
      </form>
    </div>
  );
};